/**
 * OnrampPayTab — card / bank rails (Transak, OnMeta, Banxa, …).
 * Rendered inside PaymentPage as the payment flow.
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import paymentSessionService, {
  SESSION_STATUS,
  formatErrorMessage,
} from '../services/paymentSessionService.js';
import onrampService from '../services/onrampService.js';
import supportedService from '../services/supportedService.js';
import '../pages/PaymentPage.css';

const CURRENCY_FLAGS = {
  USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧', INR: '🇮🇳', AUD: '🇦🇺',
  CAD: '🇨🇦', JPY: '🇯🇵', CHF: '🇨🇭', SGD: '🇸🇬', HKD: '🇭🇰',
  NZD: '🇳🇿', AED: '🇦🇪', THB: '🇹🇭', PHP: '🇵🇭', MYR: '🇲🇾',
  IDR: '🇮🇩', VND: '🇻🇳', KRW: '🇰🇷', BRL: '🇧🇷', MXN: '🇲🇽',
};

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', INR: '₹', AUD: 'A$',
  CAD: 'C$', JPY: '¥', CHF: 'CHF', SGD: 'S$', HKD: 'HK$',
  AED: 'د.إ', THB: '฿', PHP: '₱', MYR: 'RM', KRW: '₩',
  BRL: 'R$', MXN: 'MX$', IDR: 'Rp', VND: '₫', NZD: 'NZ$',
};

const NETWORK_COLORS = {
  ethereum: '#627eea', polygon: '#8247e5', arbitrum: '#28a0f0',
  optimism: '#ff0420', base: '#0052ff', bsc: '#c99400',
  avalanche: '#e84142', solana: '#9945ff', tron: '#eb0029',
};

const PROVIDER_LOGOS = {
  transak: 'https://cdn.onramper.com/icons/onramps/transak-colored.svg',
  banxa: 'https://cdn.onramper.com/icons/onramps/banxa-colored.svg',
  'onramp-money': 'https://onramp.money/_app/immutable/assets/logo.D-_KihkR.svg',
  'alchemy-pay': 'https://alchemypay.org/images/logo.svg',
  onmeta: 'https://onmeta.in/logo.svg',
  guardarian: 'https://guardarian.com/main-logo-dark.png',
};

const formatAmount = (amount, currency) => {
  const symbol = CURRENCY_SYMBOLS[currency] || '';
  return `${symbol}${parseFloat(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export default function OnrampPayTab({
  token,
  session: initialSession,
  onSessionChange,
}) {
  const [session, setSession] = useState(initialSession);
  const lastBubbledStatusRef = useRef(initialSession?.status);

  useEffect(() => {
    setSession((prev) => {
      if (!prev) return initialSession;
      if (prev === initialSession) return prev;
      if (prev.status === initialSession?.status) return prev;
      return initialSession;
    });
  }, [initialSession]);

  useEffect(() => {
    if (!session || !onSessionChange) return;
    if (lastBubbledStatusRef.current === session.status) return;
    lastBubbledStatusRef.current = session.status;
    onSessionChange(session);
  }, [session, onSessionChange]);

  const [paymentCurrency, setPaymentCurrency] = useState(
    initialSession?.currency || null,
  );
  const [convertedAmount, setConvertedAmount] = useState(
    initialSession?.amount || null,
  );

  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState(null);
  const [providerIcons, setProviderIcons] = useState({});

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const selectedPaymentMethodRef = useRef(selectedPaymentMethod);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const paymentDropdownRef = useRef(null);
  const paymentMethodManuallySelected = useRef(false);

  const [manualMethodTrigger, setManualMethodTrigger] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [awaitingPayment, setAwaitingPayment] = useState({
    active: false,
    providerName: null,
    widgetUrl: null,
    popupBlocked: false,
    embedAs: null,
  });

  const visiblePaymentMethods = useMemo(() => {
    if (selectedProvider?.availablePaymentMethods?.length > 0) {
      return selectedProvider.availablePaymentMethods.map((m) => ({
        id: m.paymentTypeId || m.id,
        name: m.name,
        icon: m.icon,
      }));
    }
    if (providers.length > 0) {
      const seen = new Set();
      const methods = [];
      for (const p of providers) {
        for (const m of p.availablePaymentMethods || []) {
          const id = m.paymentTypeId || m.id;
          if (!seen.has(id)) {
            seen.add(id);
            methods.push({ id, name: m.name, icon: m.icon });
          }
        }
      }
      if (methods.length > 0) return methods;
    }
    return paymentMethods;
  }, [selectedProvider, providers, paymentMethods]);

  useEffect(() => {
    selectedPaymentMethodRef.current = selectedPaymentMethod;
  }, [selectedPaymentMethod]);

  useEffect(() => {
    if (visiblePaymentMethods.length === 0) return;
    const currentMethodExists = visiblePaymentMethods.some(
      (m) => m.id === selectedPaymentMethod,
    );
    if (!currentMethodExists) {
      setSelectedPaymentMethod(visiblePaymentMethods[0].id);
    }
  }, [visiblePaymentMethods, selectedPaymentMethod]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [methodsResult, onrampsResult] = await Promise.allSettled([
          supportedService.getPaymentMethods(),
          supportedService.getAllOnramps(),
        ]);
        if (
          methodsResult.status === 'fulfilled' &&
          Array.isArray(methodsResult.value) &&
          methodsResult.value.length > 0
        ) {
          setPaymentMethods(
            methodsResult.value.map((m) => ({
              id: m.paymentTypeId || m.id,
              name: m.name,
              icon: m.icon,
            })),
          );
        }
        if (
          onrampsResult.status === 'fulfilled' &&
          Array.isArray(onrampsResult.value)
        ) {
          const icons = {};
          onrampsResult.value.forEach((o) => {
            icons[o.id] = o.icon || o.icons?.svg;
          });
          setProviderIcons(icons);
        }
      } catch (err) {
        console.error('Error loading options:', err);
      }
    };
    loadOptions();
  }, []);

  useEffect(() => {
    if (!paymentCurrency) return;
    const fetchFilteredMethods = async () => {
      paymentMethodManuallySelected.current = false;
      try {
        const methodData = await supportedService.getPaymentMethods(
          paymentCurrency,
        );
        if (Array.isArray(methodData) && methodData.length > 0) {
          const mapped = methodData.map((m) => ({
            id: m.paymentTypeId || m.id,
            name: m.name,
            icon: m.icon,
          }));
          setPaymentMethods(mapped);
          setSelectedPaymentMethod(mapped[0].id);
        }
      } catch (err) {
        console.warn('Failed to filter payment methods by fiat:', err);
      }
    };
    fetchFilteredMethods();
  }, [paymentCurrency]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        paymentDropdownRef.current &&
        !paymentDropdownRef.current.contains(e.target)
      ) {
        setShowPaymentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!session || !paymentCurrency) return;
    const skipStatuses = [
      SESSION_STATUS.AWAITING_PAYMENT,
      SESSION_STATUS.PAYMENT_VERIFYING,
      SESSION_STATUS.PROCESSING,
      SESSION_STATUS.COMPLETED,
      SESSION_STATUS.FAILED,
      SESSION_STATUS.CANCELLED,
    ];
    if (skipStatuses.includes(session.status)) return;

    const fetchQuotes = async () => {
      setQuotesLoading(true);
      setQuotesError(null);
      try {
        const orderAmount = parseFloat(session.amount);
        const response = await onrampService.compareQuotes({
          cryptoCurrency: session.cryptoCurrency,
          network: session.network,
          paymentMethod: paymentMethodManuallySelected.current
            ? selectedPaymentMethodRef.current
            : undefined,
          isBuyOrSell: 'BUY',
          fiatCurrency: paymentCurrency,
          fiatAmount: orderAmount,
        });
        const { quotes = [], meta = {} } = response || {};
        const validQuotes = quotes.filter((q) => q && q.cryptoAmount > 0);
        if (validQuotes.length === 0 && meta.providersQueried?.length > 0) {
          setQuotesError(
            `No providers available for ${paymentCurrency} at this time.`,
          );
          setProviders([]);
          setSelectedProvider(null);
          setConvertedAmount(orderAmount);
          return;
        }
        validQuotes.sort(
          (a, b) => (b.cryptoAmount || 0) - (a.cryptoAmount || 0),
        );
        const bestPayout = validQuotes[0]?.cryptoAmount || 0;
        const quotesWithBest = validQuotes.map((q) => ({
          ...q,
          isBestPrice: bestPayout > 0 && q.cryptoAmount === bestPayout,
        }));
        setProviders(quotesWithBest);
        setSelectedProvider((prev) => {
          if (prev) {
            const stillExists = quotesWithBest.find(
              (q) => q.provider === prev.provider,
            );
            if (stillExists) return stillExists;
          }
          return quotesWithBest[0] || null;
        });
        setConvertedAmount(orderAmount);
      } catch (err) {
        console.error('Error fetching quotes:', err);
        setQuotesError('Unable to fetch quotes. Please try again.');
        setProviders([]);
        setSelectedProvider(null);
      } finally {
        setQuotesLoading(false);
      }
    };

    fetchQuotes();
  }, [session, paymentCurrency, manualMethodTrigger]); // eslint-disable-line

  useEffect(() => {
    if (
      session?.status === SESSION_STATUS.AWAITING_PAYMENT &&
      !awaitingPayment.active
    ) {
      setAwaitingPayment({
        active: true,
        providerName: 'Payment Provider',
        widgetUrl: session.widgetUrl || null,
        popupBlocked: false,
        embedAs: session.embedAs || null,
      });
    }
  }, [session?.status, session?.widgetUrl, session?.embedAs, awaitingPayment.active]);

  const lastStatusCheckRef = useRef(0);
  const checkSessionStatus = useCallback(async () => {
    if (!token) return false;
    const now = Date.now();
    if (now - lastStatusCheckRef.current < 4000) return false;
    lastStatusCheckRef.current = now;
    try {
      const statusResult = await paymentSessionService.getSessionStatus(token);
      if (statusResult.status === SESSION_STATUS.COMPLETED) {
        setSession((prev) => ({ ...prev, status: SESSION_STATUS.COMPLETED }));
        if (session?.redirectUrl) {
          setTimeout(() => {
            window.location.href = session.redirectUrl;
          }, 2000);
        }
        return true;
      } else if (
        statusResult.status === SESSION_STATUS.FAILED ||
        statusResult.status === SESSION_STATUS.CANCELLED
      ) {
        setSession((prev) => ({ ...prev, status: statusResult.status }));
        return true;
      } else if (
        statusResult.status === SESSION_STATUS.PROCESSING ||
        statusResult.status === SESSION_STATUS.PAYMENT_VERIFYING
      ) {
        setSession((prev) => ({ ...prev, status: SESSION_STATUS.PROCESSING }));
      }
      return false;
    } catch (err) {
      console.warn('Status check failed:', err);
      return false;
    }
  }, [token, session?.redirectUrl]);

  useEffect(() => {
    if (!token) return;
    const isPollingStatus =
      session?.status === SESSION_STATUS.AWAITING_PAYMENT ||
      session?.status === SESSION_STATUS.PROCESSING ||
      session?.status === SESSION_STATUS.PAYMENT_VERIFYING;
    if (!isPollingStatus) return;
    const initialDelay = setTimeout(() => checkSessionStatus(), 5000);
    const poll = setInterval(() => {
      checkSessionStatus();
    }, 15000);
    return () => {
      clearTimeout(initialDelay);
      clearInterval(poll);
    };
  }, [token, session?.status, checkSessionStatus]);

  useEffect(() => {
    if (!token) return;
    const isPaymentInProgress =
      session?.status === SESSION_STATUS.AWAITING_PAYMENT ||
      session?.status === SESSION_STATUS.PROCESSING ||
      session?.status === SESSION_STATUS.PAYMENT_VERIFYING;
    if (!isPaymentInProgress) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSessionStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, session?.status, checkSessionStatus]);

  const handleCheckout = async () => {
    if (!selectedProvider) return;
    setCheckoutLoading(true);
    try {
      const result = await paymentSessionService.initiateCheckout(token, {
        provider: selectedProvider.provider,
        paymentMethod: selectedPaymentMethod || undefined,
        paymentCurrency,
        paymentAmount: convertedAmount,
      });
      if (result && result.widgetUrl) {
        const providerDisplayName =
          selectedProvider.provider.charAt(0).toUpperCase() +
          selectedProvider.provider.slice(1);
        const embedAs = result.embedAs || 'tab';

        if (embedAs === 'iframe') {
          // In-page iframe (e.g. Guardarian) — no popup, overlay renders <iframe>.
          setAwaitingPayment({
            active: true,
            providerName: providerDisplayName,
            widgetUrl: result.widgetUrl,
            popupBlocked: false,
            embedAs: 'iframe',
          });
        } else {
          const newTab = window.open(result.widgetUrl, '_blank', 'noopener,noreferrer');
          const popupBlocked = !newTab || newTab.closed;
          setAwaitingPayment({
            active: true,
            providerName: providerDisplayName,
            widgetUrl: result.widgetUrl,
            popupBlocked,
            embedAs: 'tab',
          });
        }
        setSession((prev) => ({
          ...prev,
          status: SESSION_STATUS.AWAITING_PAYMENT,
        }));
      } else {
        alert('Failed to initiate payment');
        setCheckoutLoading(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert(formatErrorMessage(err?.message) || 'Failed to initiate payment');
      setCheckoutLoading(false);
    }
  };

  if (!session) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
        Loading payment options…
      </div>
    );
  }

  return (
    <div className="simple-payment-card" style={{ boxShadow: 'none' }}>
      <div className="simple-payment-header">
        <div className="simple-logo">
          <svg viewBox="0 0 40 40" fill="none">
            <circle cx="12" cy="20" r="8" stroke="currentColor" strokeWidth="3" fill="none" />
            <circle cx="28" cy="20" r="8" stroke="currentColor" strokeWidth="3" fill="none" />
          </svg>
        </div>
        <h1 className="simple-title">Complete Payment</h1>
      </div>

      <div className="simple-amount-section">
        <div className="order-amount-label">Order Amount</div>
        <div className="order-amount-value">
          {formatAmount(session.amount, session.currency)}
          <span className="order-currency">{session.currency}</span>
        </div>
      </div>

      <div className="simple-pay-section">
        <div className="pay-label">Pay in</div>
        <div className="currency-select-container">
          <div className="currency-select-btn" style={{ cursor: 'default' }}>
            <span className="currency-flag">
              {CURRENCY_FLAGS[paymentCurrency] || '💵'}
            </span>
            <span className="currency-name">{paymentCurrency}</span>
          </div>
        </div>
      </div>

      <div className="network-crypto-info">
        <div className="info-row">
          <span className="info-label">Crypto</span>
          <span className="info-value">{session.cryptoCurrency}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Network</span>
          <span className="info-value">
            <span
              className="network-dot"
              style={{
                background: NETWORK_COLORS[session.network] || '#7c3aed',
              }}
            />
            {session.networkDisplayName || session.network}
          </span>
        </div>
      </div>

      <div className="payment-method-section">
        <div className="method-label">Payment Method</div>
        <div className="method-select-container" ref={paymentDropdownRef}>
          <button
            className="method-select-btn"
            onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
          >
            <span className="method-icon">💳</span>
            <span className="method-name">
              {visiblePaymentMethods.find(
                (m) => m.id === selectedPaymentMethod,
              )?.name ||
                visiblePaymentMethods[0]?.name ||
                'Select Method'}
            </span>
            <span className="dropdown-arrow">▼</span>
          </button>
          {showPaymentDropdown && (
            <div className="method-dropdown">
              {visiblePaymentMethods.map((method) => (
                <button
                  key={method.id}
                  className={`method-option ${method.id === selectedPaymentMethod ? 'selected' : ''}`}
                  onClick={() => {
                    paymentMethodManuallySelected.current = true;
                    setSelectedPaymentMethod(method.id);
                    setManualMethodTrigger((prev) => prev + 1);
                    setShowPaymentDropdown(false);
                  }}
                >
                  <span className="method-icon">💳</span>
                  <span className="method-name">{method.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="providers-section-simple">
        <div className="providers-label">Select Provider</div>
        {quotesLoading ? (
          <div className="providers-loading-simple">
            <div className="provider-skeleton-simple" />
            <div className="provider-skeleton-simple" />
          </div>
        ) : providers.length === 0 ? (
          <div className="providers-empty-simple">
            <p className="empty-message">
              {quotesError || 'No providers available for this selection.'}
            </p>
          </div>
        ) : (
          <div className="providers-list-simple">
            {providers.map((provider) => (
              <div
                key={provider.provider}
                className={`provider-card-simple ${selectedProvider?.provider === provider.provider ? 'selected' : ''}`}
                onClick={() => setSelectedProvider(provider)}
              >
                <div className="provider-left-simple">
                  <div className="provider-logo-simple">
                    {(providerIcons[provider.provider] ||
                      PROVIDER_LOGOS[provider.provider]) ? (
                      <img
                        src={
                          providerIcons[provider.provider] ||
                          PROVIDER_LOGOS[provider.provider]
                        }
                        alt={provider.provider}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span
                      style={{
                        display:
                          providerIcons[provider.provider] ||
                          PROVIDER_LOGOS[provider.provider]
                            ? 'none'
                            : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        fontWeight: 600,
                        fontSize: '14px',
                      }}
                    >
                      {provider.provider.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="provider-name-simple">
                    {provider.provider.charAt(0).toUpperCase() +
                      provider.provider.slice(1)}
                    {provider.isBestPrice && (
                      <span className="best-badge">Best Rate</span>
                    )}
                  </div>
                </div>
                <div className="provider-fee-simple">
                  {provider.fees?.total > 0
                    ? `Fee: ${formatAmount(provider.fees.total, paymentCurrency)}`
                    : 'Low fees'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        className={`pay-button-simple ${checkoutLoading ? 'loading' : ''}`}
        onClick={handleCheckout}
        disabled={
          checkoutLoading ||
          !selectedProvider ||
          quotesLoading ||
          awaitingPayment.active ||
          session?.status === SESSION_STATUS.AWAITING_PAYMENT
        }
      >
        {checkoutLoading ? (
          <span className="btn-loading" />
        ) : (
          <>Pay {formatAmount(convertedAmount || session.amount, paymentCurrency)}</>
        )}
      </button>

      <div className="security-note">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="lock-icon"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
        <span>Secure payment powered by trusted providers</span>
      </div>

      {awaitingPayment.active &&
        (session?.status === SESSION_STATUS.AWAITING_PAYMENT ||
          session?.status === SESSION_STATUS.PROCESSING) && (
          <div className="awaiting-payment-overlay">
            {awaitingPayment.embedAs === 'iframe' &&
            awaitingPayment.widgetUrl &&
            session?.status !== SESSION_STATUS.PROCESSING ? (
              /* In-page iframe checkout (Guardarian and any future iframe
                 providers). No `sandbox` attribute — would strip camera /
                 microphone access for KYC selfie + 3-D-Secure. */
              <div
                className="awaiting-payment-content awaiting-payment-iframe-wrap"
                style={{
                  width: '100%',
                  height: '100%',
                  padding: 0,
                  display: 'flex',
                }}
              >
                <iframe
                  src={awaitingPayment.widgetUrl}
                  title={`${awaitingPayment.providerName} checkout`}
                  allow="camera; microphone; fullscreen; payment; accelerometer; autoplay; gyroscope"
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '600px',
                    border: 0,
                  }}
                />
              </div>
            ) : (
            <div className="awaiting-payment-content">
              <div className="awaiting-pulse-container">
                <div className="awaiting-pulse" />
                <div className="awaiting-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                </div>
              </div>
              <h2 className="awaiting-title">
                {session?.status === SESSION_STATUS.PROCESSING
                  ? 'Processing Your Payment'
                  : 'Complete Your Payment'}
              </h2>
              <p className="awaiting-message">
                {session?.status === SESSION_STATUS.PROCESSING ? (
                  'Your payment has been received and is being processed.'
                ) : (
                  <>
                    Please complete your payment on the{' '}
                    <strong>{awaitingPayment.providerName}</strong> page.
                  </>
                )}
              </p>
              {session?.status !== SESSION_STATUS.PROCESSING &&
                awaitingPayment.widgetUrl && (
                  <a
                    href={awaitingPayment.widgetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="awaiting-open-link"
                  >
                    Open {awaitingPayment.providerName} Payment Page
                  </a>
                )}
              <p className="awaiting-hint">
                {session?.status === SESSION_STATUS.PROCESSING
                  ? 'Almost done! This page will update once confirmed.'
                  : 'Once completed, this page will update automatically.'}
              </p>
            </div>
            )}
          </div>
        )}
    </div>
  );
}
